import { v4 as uuid } from 'uuid'
import type { Clip, Track } from '@/types'
import { MAX_TRACK_COUNT, quantizeToFrame } from '@/lib/constants'
import { clipTimingError, sourceTimeAt } from '@/types/timeline-editing'
import { resolveOverlaps } from '@/lib/timeline'
import { normalizeClipUpdate } from '@/lib/timelineClipUpdate'
import { pastedClipFromItem, type TimelineClipboardItem } from '@/lib/timelineClipboard'

export interface TimelineClipboard {
  /** Readable system clipboard fallback; never used to reconstruct timeline edits. */
  text?: string
  projectId: string
  items: TimelineClipboardItem[]
  sourceTime: number
  sourceTrackIndex: number
  tracks: Array<Pick<Track, 'index' | 'type'> & { position: number }>
}

export type TimelinePastePlan =
  { error: string } | { tracks: Track[]; writes: Clip[]; deletes: string[]; pastedIds: string[] }

/** Plan against the live document before mutating it: failures never leave a partial paste. */
export function planTimelinePaste(
  clipboard: TimelineClipboard,
  target: { sequenceId: string; time: number; trackIndex?: number },
  tracks: ReadonlyMap<string, Track>,
  clips: ReadonlyMap<string, Clip>,
  nextId: () => string = uuid,
): TimelinePastePlan {
  const sequenceTracks = [...tracks.values()].filter(track => track.sequenceId === target.sequenceId)
  const createdTracks: Track[] = []
  const mapping = new Map<number, number>()
  const anchor = clipboard.tracks.find(track => track.index === clipboard.sourceTrackIndex)
  const requestedTrack = sequenceTracks.find(track => track.index === target.trackIndex)
  if (target.trackIndex !== undefined && (!requestedTrack || requestedTrack.type !== anchor?.type)) {
    return { error: 'Choose a track of the same type, then paste again.' }
  }
  const requestedPosition = requestedTrack
    ? sequenceTracks
        .filter(track => track.type === requestedTrack.type)
        .sort((a, b) => a.index - b.index)
        .findIndex(track => track.id === requestedTrack.id)
    : undefined
  const positionDelta = requestedPosition !== undefined && anchor ? requestedPosition - anchor.position : 0

  for (const source of clipboard.tracks) {
    const ordinal = source.position + (source.type === anchor?.type ? positionDelta : 0)
    if (ordinal < 0)
      return { error: 'There is not enough room below this track. Choose another track and paste again.' }
    const matching = sequenceTracks.filter(track => track.type === source.type).sort((a, b) => a.index - b.index)
    while (matching.length <= ordinal) {
      const used = new Set(sequenceTracks.map(track => track.index))
      let index = Math.max(-1, ...used) + 1
      if (index >= MAX_TRACK_COUNT) {
        index =
          Array.from({ length: MAX_TRACK_COUNT }, (_, candidate) => candidate).find(
            candidate => !used.has(candidate) && candidate > Math.max(-1, ...matching.map(track => track.index)),
          ) ?? MAX_TRACK_COUNT
      }
      if (index >= MAX_TRACK_COUNT)
        return { error: 'This sequence has no room for more tracks. Remove an unused track and try again.' }
      const track: Track = {
        id: nextId(),
        sequenceId: target.sequenceId,
        index,
        type: source.type,
        name: '',
        muted: false,
        solo: false,
        locked: false,
        isVisible: true,
      }
      matching.push(track)
      sequenceTracks.push(track)
      createdTracks.push(track)
    }
    const track = matching[ordinal]
    if (track.locked) return { error: 'A destination track is locked. Unlock it and paste again.' }
    mapping.set(source.index, track.index)
  }

  const working = new Map(clips)
  const pastedIds: string[] = []
  const links = new Map<string, string>()
  const splitLinkGroups = new Map<string, string>()
  for (const item of clipboard.items) {
    const trackIndex = mapping.get(clipboard.sourceTrackIndex + item.trackOffset)
    if (trackIndex === undefined) return { error: 'The copied tracks could not be read. Copy the clips again.' }
    if (item.linkGroupId && !links.has(item.linkGroupId)) links.set(item.linkGroupId, nextId())
    const clip: Clip = {
      ...pastedClipFromItem(item, {
        startOnTimeline: quantizeToFrame(target.time + item.startOffset),
        trackIndex,
        linkGroupId: item.linkGroupId ? links.get(item.linkGroupId) : undefined,
      }),
      id: nextId(),
      sequenceId: target.sequenceId,
    }
    if (clipTimingError(clip)) return { error: 'The copied clip timing could not be read. Copy the clips again.' }
    for (const resolution of resolveOverlaps(
      clip.startOnTimeline,
      clip.startOnTimeline + clip.duration,
      trackIndex,
      target.sequenceId,
      working,
      new Set(pastedIds),
      splitLinkGroups,
    )) {
      const existing = working.get(resolution.clipId)!
      if (resolution.type === 'delete') working.delete(existing.id)
      else if (resolution.type === 'trim-left' && resolution.trimLeft)
        working.set(existing.id, { ...existing, ...normalizeClipUpdate(existing, resolution.trimLeft) })
      else if (resolution.type === 'trim-right' && resolution.trimRight)
        working.set(existing.id, { ...existing, ...normalizeClipUpdate(existing, resolution.trimRight) })
      else if (resolution.split) {
        const duration = resolution.split.splitTicks - existing.startOnTimeline
        working.set(existing.id, {
          ...existing,
          ...normalizeClipUpdate(existing, {
            duration,
            trimOut: existing.loop ? existing.trimOut : sourceTimeAt(existing, duration),
          }),
        })
        const second = { ...resolution.split.secondHalf, id: nextId() }
        working.set(second.id, second)
      }
    }
    working.set(clip.id, clip)
    pastedIds.push(clip.id)
  }
  return {
    tracks: createdTracks,
    writes: [...working.values()].filter(clip => clips.get(clip.id) !== clip),
    deletes: [...clips.keys()].filter(id => !working.has(id)),
    pastedIds,
  }
}
